import { useState, useEffect, useCallback } from 'react';
import { mastersApi } from '@/features/masters/api';
import { TruckDriver, TruckDriverInsert, TruckDriverUpdate } from '@/features/masters/types';

/**
 * トラック運転手データを管理するフック (FastAPI経由)
 */
export function useTruckDrivers() {
  const [truckDrivers, setTruckDrivers] = useState<TruckDriver[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // データを取得する関数
  const fetchTruckDrivers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await mastersApi.truckDrivers.getAll();
      setTruckDrivers(data);
    } catch (err: unknown) {
      console.error('Failed to fetch truck drivers:', err);
      const errorMessage = err instanceof Error && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data && typeof err.response.data.message === 'string'
        ? err.response.data.message
        : 'トラック運転手データの取得に失敗しました';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初回読み込み
  useEffect(() => {
    fetchTruckDrivers();
  }, [fetchTruckDrivers]);

  // トラック運転手作成
  const createEntity = async (data: TruckDriverInsert) => {
    try {
      const result = await mastersApi.truckDrivers.create(data);
      await fetchTruckDrivers(); // データを再取得
      return result;
    } catch (error) {
      console.error('Error creating truck driver:', error);
      throw error;
    }
  };

  // トラック運転手更新
  const updateEntity = async (id: string, data: TruckDriverUpdate) => {
    try {
      const result = await mastersApi.truckDrivers.update(id, data);
      await fetchTruckDrivers(); // データを再取得
      return result;
    } catch (error) {
      console.error('Error updating truck driver:', error);
      throw error;
    }
  };

  // トラック運転手削除
  const deleteEntity = async (id: string) => {
    try {
      await mastersApi.truckDrivers.delete(id);
      await fetchTruckDrivers(); // データを再取得
      return true;
    } catch (error) {
      console.error('Error deleting truck driver:', error);
      throw error;
    }
  };

  return {
    data: truckDrivers,
    items: truckDrivers,
    isLoading,
    error,
    // 互換性のための追加プロパティ
    refreshData: fetchTruckDrivers,
    createEntity,
    updateEntity,
    deleteEntity,
  };
}
